import { defineEntityConstellationDerivation, defineEntityItemDerivation } from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import type { EntityConstellation } from '@toolproof-core/visualization/_contract/constellation';
import type { EntityItem } from '@toolproof-core/visualization/_contract/item';
import type { EntityConstellationDerivation, EntityItemCollectionDerivation } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { COLOR_PALETTES } from '@/workflow/_data/config/fragments/colorPalettes';
import {
    buildToolStepOuterRimCollectionKey,
    buildToolStepOuterRimConstellationKey,
    buildToolStepOuterRimItemKey,
} from '@/workflow/_pure/keyCodec';
import { buildToolStepItemCollectionDerivation } from '@/workflow/_pure/derivation/collections';
import type { BaseConfig } from '@/workflow/_contract/config';
import type { WorkflowKeyScope } from '@/workflow/_pure/keyCodec';

export function deriveToolStepOuterRimDerivation(args: {
    ringRadii: readonly number[];
    ringGap: number;
    scope: WorkflowKeyScope;
    config: BaseConfig;
}): {
    itemCollectionDerivation: EntityItemCollectionDerivation;
    constellationDerivation: EntityConstellationDerivation;
} | undefined {
    const lastToolStepRadius = args.ringRadii.at(-1);
    if (lastToolStepRadius === undefined) return undefined;

    const { radialWidth, height, opacity } = args.config.domainPresentation.entities.toolSteps.outerRim;
    if (!Number.isFinite(radialWidth) || radialWidth <= 0
        || !Number.isFinite(height) || height <= 0
        || !Number.isFinite(opacity) || opacity < 0 || opacity > 1
        || !Number.isFinite(args.ringGap) || args.ringGap <= 0
        || !args.ringRadii.every((radius) => Number.isFinite(radius) && radius > 0)) {
        throw new Error('Invalid ToolStep outer rim geometry or opacity.');
    }
    const centerlineRadius = lastToolStepRadius + args.ringGap;
    const halfWidth = radialWidth / 2;
    const innerRadius = centerlineRadius - halfWidth;
    if (innerRadius <= lastToolStepRadius) {
        throw new Error('ToolStep outer rim width must clear the outermost ToolStep radius.');
    }
    const outerRadius = centerlineRadius + halfWidth;
    const color = COLOR_PALETTES[args.config.colorPalette].toolSteps.outerRim;
    const itemKey = buildToolStepOuterRimItemKey(args.scope);
    const collectionKey = buildToolStepOuterRimCollectionKey(args.scope);
    const constellationKey = buildToolStepOuterRimConstellationKey(args.scope);
    const item: EntityItem = {
        itemKind: 'entity',
        itemKey,
        name: 'ToolStep outer rim',
        description: '',
    };

    const itemDerivation = defineEntityItemDerivation({
        item,
        parametricState: {
            visualStyle: { color, opacity },
            interaction: { enabled: false },
            shape: {
                shapeKind: 'cylinder-wall',
                labelHostCapability: 'above-only',
                innerRadius,
                outerRadius,
                height,
                axis: [0, 1, 0],
                arcSegments: 192,
                innerOpacity: 1,
                outerOpacity: 1,
                cutaway: false,
                anchorsByKey: {
                    center: {
                        anchorKey: 'center',
                        localPosition: [0, 0, 0],
                    },
                },
            },
        },
    });
    const itemCollectionDerivation = buildToolStepItemCollectionDerivation({
        itemCollectionKey: collectionKey,
        itemDerivations: [itemDerivation],
    });
    const constellation: EntityConstellation = {
        constellationKind: 'entity',
        constellationKey,
        itemCollectionKey: collectionKey,
        anchor: { itemKey, anchorKey: 'center' },
    };
    const constellationDerivation = defineEntityConstellationDerivation({
        constellation,
        parametricState: {
            layout: {
                layoutKind: 'spoke',
                anchor: { anchorKind: 'origin' },
                originNearestPosition: [0, 0, 0],
                direction: [0, 0, 1],
                spacing: 0,
            },
        },
    });

    return { itemCollectionDerivation, constellationDerivation };
}
